import { liveSince, type FeedItem } from '@/components/announcementFeed';
import { TickerChip } from '@/components/AnnouncementTicker';
import { useNow } from '@/hooks/useNow';
import { AnnouncementSpotlight } from '@/screens/dashboard/AnnouncementBoard';
import { useAuth } from '@/stores/auth';

/**
 * What people will get, drawn with the dashboard's own ticker chip and
 * announcement spotlight, so it cannot drift from the real thing as either
 * changes.
 */
export function AnnouncementPreview({
  item,
  pictureSrc,
  seenBy,
  shows,
}: {
  item: FeedItem;
  pictureSrc: string | undefined;
  seenBy: string;
  shows: string;
}) {
  const user = useAuth((s) => s.user);
  const clock = useNow(60_000);
  const empty = !item.headlineEn.trim();

  // As it will look the moment it goes live: put up by whoever is writing it,
  // just now. A notice scheduled for later is shown as of its start.
  const shown: FeedItem = {
    ...item,
    headlineEn: empty ? 'Your headline appears here' : item.headlineEn,
    publishedAt: item.publishedAt ?? new Date(clock).toISOString(),
    postedBy: item.postedBy ?? (user ? { name: user.fullName, nameUr: user.fullNameUr } : null),
  };
  const now = Math.max(clock, liveSince(shown)?.getTime() ?? 0);

  return (
    <aside className="composer__preview" aria-label="Preview">
      <p className="composer__eyebrow">How it will look</p>

      <div className={`composer__previewbox${empty ? ' composer__previewbox--empty' : ''}`} aria-hidden="true">
        <p className="composer__caption">In the strip at the top of the Dashboard</p>
        <div className="ticker composer__ticker">
          <span className="ticker__label">Updates</span>
          <div className="composer__tickerline">
            <TickerChip item={shown} language="en" onOpen={() => {}} />
          </div>
        </div>

        <p className="composer__caption">On the Dashboard, when it is the first notice</p>
        <div className="board composer__board">
          <AnnouncementSpotlight
            item={shown}
            language="en"
            now={now}
            onOpen={() => {}}
            pictureSrc={pictureSrc}
            preview
          />
        </div>
      </div>

      <dl className="composer__facts">
        <div>
          <dt>Seen by</dt>
          <dd>{seenBy}</dd>
        </div>
        <div>
          <dt>Shows</dt>
          <dd>{shows}</dd>
        </div>
      </dl>
    </aside>
  );
}
